import React, { useMemo } from 'react';
import {
  AbsoluteFill,
  Html5Video,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import { Video as WebCodecsVideo, type MediaErrorAction } from '@remotion/media';
import { createTikTokStyleCaptions, type Caption, type TikTokPage } from '@remotion/captions';
import { repaginate } from '../lib/captions';
import {
  captionFontSize,
  getStyle,
  strokeShadow,
  type CaptionStyleId,
} from './captionStyles';

export type SubtitledVideoProps = {
  src: string;
  captions: Caption[];
  styleId: CaptionStyleId;
  accent: string;
  /** Resolved CSS font-family. Loaded by the app before rendering starts. */
  fontFamily: string;
  /**
   * Which media element backs the video.
   *
   * The export must use 'webcodecs': @remotion/web-renderer cannot use the
   * fallback components, which need server-side capabilities the browser does
   * not have.
   *
   * The preview uses 'html5', because scrubbing is what it is for and a plain
   * video element seeks reliably. The WebCodecs tag is still experimental and
   * was leaving a frozen frame after a seek — the decoder never advanced past
   * it while the composition kept ticking. Remotion's own fallback path uses
   * Html5Video during preview for the same reason.
   *
   * Both draw the same source pixels, so the captions composited over them are
   * identical either way.
   */
  videoBackend?: 'webcodecs' | 'html5';
};

const Page: React.FC<{
  page: TikTokPage;
  styleId: CaptionStyleId;
  accent: string;
  timeMs: number;
  fontFamily: string;
}> = ({ page, styleId, accent, timeMs, fontFamily }) => {
  const { fps, height } = useVideoConfig();
  const fontSize = captionFontSize(styleId, height);

  // This component is not wrapped in a <Sequence>, so useCurrentFrame() would
  // report the global frame. Derive a page-local frame instead, otherwise the
  // entrance only ever plays for the first page.
  const localFrame = ((timeMs - page.startMs) / 1000) * fps;

  // Page entrance. `clean` stays still — a broadcast bar that bounces is wrong.
  const enter =
    styleId === 'clean'
      ? interpolate(localFrame, [0, 4], [0, 1], { extrapolateRight: 'clamp' })
      : spring({ frame: localFrame, fps, config: { damping: 200 }, durationInFrames: 8 });

  const stroke = strokeShadow(Math.max(2, Math.round(fontSize * 0.055)));

  const common: React.CSSProperties = {
    fontFamily,
    fontSize,
    fontWeight: 800,
    letterSpacing: '-0.02em',
    textAlign: 'center',
    lineHeight: 1.25,
  };

  if (styleId === 'clean') {
    return (
      <div
        style={{
          ...common,
          fontWeight: 600,
          fontSize: fontSize,
          opacity: enter,
          background: 'rgba(0,0,0,0.72)',
          color: '#fff',
          padding: `${fontSize * 0.32}px ${fontSize * 0.6}px`,
          borderRadius: fontSize * 0.22,
          maxWidth: '80%',
        }}
      >
        {page.text}
      </div>
    );
  }

  return (
    <div
      style={{
        ...common,
        opacity: enter,
        transform: `scale(${0.9 + enter * 0.1})`,
        maxWidth: '86%',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        // Must stay wider than what the active word's scale consumes on each
        // side, or the highlighted word visually collides with its neighbour.
        gap: `${fontSize * 0.12}px ${fontSize * 0.34}px`,
      }}
    >
      {page.tokens.map((token, i) => {
        const spoken = timeMs >= token.fromMs;
        const active = spoken && timeMs < token.toMs;

        if (styleId === 'karaoke') {
          return (
            <span
              key={i}
              style={{
                color: spoken ? accent : '#FFFFFF',
                textShadow: stroke,
              }}
            >
              {token.text.trim()}
            </span>
          );
        }

        // 'tiktok': the active word pops and takes the accent colour.
        return (
          <span
            key={i}
            style={{
              display: 'inline-block',
              color: active ? accent : '#FFFFFF',
              textShadow: stroke,
              transform: `scale(${active ? 1.07 : 1})`,
              transformOrigin: 'center bottom',
            }}
          >
            {token.text.trim()}
          </span>
        );
      })}
    </div>
  );
};

const videoStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

/**
 * Shaped to satisfy both backends. Returns undefined to keep Remotion's own
 * error handling — this only makes the failure visible, which it was not
 * before: a dead decoder simply left the last frame on screen.
 */
const reportVideoError = (err: Error): MediaErrorAction | undefined => {
  // eslint-disable-next-line no-console
  console.error('[undertone] video playback failed', err);
  return undefined;
};

export const SubtitledVideo: React.FC<SubtitledVideoProps> = ({
  src,
  captions,
  styleId,
  accent,
  fontFamily,
  videoBackend = 'webcodecs',
}) => {
  const frame = useCurrentFrame();
  const { fps, height } = useVideoConfig();
  const style = getStyle(styleId);

  const pages = useMemo(() => {
    const { pages: raw } = createTikTokStyleCaptions({
      captions,
      combineTokensWithinMilliseconds: style.combineWithinMs,
    });
    return repaginate(raw, style.maxCharsPerPage);
  }, [captions, style.combineWithinMs, style.maxCharsPerPage]);

  const timeMs = (frame / fps) * 1000;

  // Only one page is on screen at a time; find it rather than mounting all.
  const current = useMemo(() => {
    let lo = 0;
    let hi = pages.length - 1;
    let found: TikTokPage | null = null;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const p = pages[mid];
      if (timeMs < p.startMs) hi = mid - 1;
      else if (timeMs >= p.startMs + p.durationMs) lo = mid + 1;
      else {
        found = p;
        break;
      }
    }
    return found;
  }, [pages, timeMs]);

  const bottom = styleId === 'clean' ? height * 0.07 : height * 0.14;

  return (
    <AbsoluteFill style={{ backgroundColor: '#000' }}>
      {videoBackend === 'html5' ? (
        <Html5Video src={src} style={videoStyle} onError={reportVideoError} />
      ) : (
        <WebCodecsVideo
          src={src}
          style={videoStyle}
          // Without these a decode failure is silent: the captions and the
          // frame counter keep running over a stale frame, which reads as the
          // video having frozen rather than as an error.
          logLevel="warn"
          onError={reportVideoError}
        />
      )}

      {current ? (
        <AbsoluteFill
          style={{
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingBottom: bottom,
          }}
        >
          {/* Keyed so each page remounts and replays its entrance spring. */}
          <Page
            key={current.startMs}
            page={current}
            styleId={styleId}
            accent={accent}
            timeMs={timeMs}
            fontFamily={fontFamily}
          />
        </AbsoluteFill>
      ) : null}
    </AbsoluteFill>
  );
};
